import { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck } from "lucide-react";
import { adminStats } from "../lib/api";
import { ProjectsTab } from "../components/admin/ProjectsTab";
import { NotesTab } from "../components/admin/NotesTab";
import { InboxTab } from "../components/admin/InboxTab";
import { SiteTab } from "../components/admin/SiteTab";
import { AccountBar } from "../components/admin/AccountBar";
import { Seo } from "../components/Seo";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    adminStats().then(setStats).catch(() => setStats(null));
  }, [refreshKey]);

  return (
    <div className="py-16 px-6 bg-deep min-h-[70vh]" data-testid="admin-page">
      <Seo title="Admin Workspace — oVu Cyber" description="Owner-only workspace." noindex />
      <div className="max-w-6xl mx-auto">
        {/* Login is enforced server-side on every admin route */}
        <div className="mb-6 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/40 flex items-start gap-3" data-testid="admin-auth-notice">
          <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          <p className="font-mono text-xs text-emerald-300 leading-relaxed">
            PROTECTED — every admin action requires your login, checked on the server.
            Change your password anytime from this page.
          </p>
        </div>

        <div className="p-6 md:p-8 rounded-xl bg-ink border border-white/10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cyber"></span>
                <h1 className="font-display text-xl text-white font-bold uppercase">oVu Project Desk</h1>
              </div>
              <p className="font-mono text-xs text-dim mt-1">Owner workspace — projects, learning notes, and contact inbox</p>
            </div>
            <AccountBar />
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-xs mb-8" data-testid="admin-stats">
            <div className="p-4 rounded bg-panelalt border border-white/5">
              <span className="text-dim block mb-1">Published</span>
              <span className="text-white font-bold text-xl" data-testid="stat-published">{stats ? stats.published : "…"}</span>
              <span className="text-[10px] text-emerald-400 block mt-1">Live on public site</span>
            </div>
            <div className="p-4 rounded bg-panelalt border border-white/5">
              <span className="text-dim block mb-1">Drafts / In-Progress</span>
              <span className="text-white font-bold text-xl" data-testid="stat-drafts">{stats ? stats.drafts : "…"}</span>
              <span className="text-[10px] text-amber-400 block mt-1">Hidden from public</span>
            </div>
            <div className="p-4 rounded bg-panelalt border border-white/5">
              <span className="text-dim block mb-1">Learning Notes</span>
              <span className="text-white font-bold text-xl" data-testid="stat-notes">{stats ? stats.notes : "…"}</span>
              <span className="text-[10px] text-dim block mt-1">Private for now</span>
            </div>
            <div className="p-4 rounded bg-panelalt border border-white/5">
              <span className="text-dim block mb-1">Unread Messages</span>
              <span className="text-white font-bold text-xl" data-testid="stat-unread">{stats ? stats.unread_messages : "…"}</span>
              <span className="text-[10px] text-cyber block mt-1">From contact form</span>
            </div>
          </div>

          <Tabs defaultValue="projects">
            <TabsList className="bg-panelalt border border-white/10 mb-6">
              <TabsTrigger value="projects" data-testid="admin-tab-projects" className="font-mono text-xs data-[state=active]:bg-cyber data-[state=active]:text-[#0b0f19]">Projects</TabsTrigger>
              <TabsTrigger value="notes" data-testid="admin-tab-notes" className="font-mono text-xs data-[state=active]:bg-cyber data-[state=active]:text-[#0b0f19]">Notes</TabsTrigger>
              <TabsTrigger value="inbox" data-testid="admin-tab-inbox" className="font-mono text-xs data-[state=active]:bg-cyber data-[state=active]:text-[#0b0f19]">Inbox</TabsTrigger>
              <TabsTrigger value="site" data-testid="admin-tab-site" className="font-mono text-xs data-[state=active]:bg-cyber data-[state=active]:text-[#0b0f19]">Site Text</TabsTrigger>
            </TabsList>
            <TabsContent value="projects"><ProjectsTab onMutate={refresh} /></TabsContent>
            <TabsContent value="notes"><NotesTab onMutate={refresh} /></TabsContent>
            <TabsContent value="inbox"><InboxTab onMutate={refresh} /></TabsContent>
            <TabsContent value="site"><SiteTab /></TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
